import { api } from '../api.js';
import { openPlaidLink } from '../plaid-link.js';
import { createUpdateChecker } from '../update-checker.js';
import { h, money, isDebt, ago, openSheet } from '../ui.js';

// Settings: the bank connections (Plaid) and the accounts they bring in, manual
// accounts for what Plaid can't see, the order accounts appear on Home, and the app.

declare const __APP_VERSION__: string;
const APP_VERSION = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev';

interface ManagedAccount {
  id: number; name: string; type: string; balance: number; institution: string;
  connection_id: number | null; in_net: boolean; archived: boolean;
}
interface Connection {
  id: number; label: string; status: string; source: string;
  last_synced_at: number | null; last_error: string | null; issue: string | null;
  accounts: ManagedAccount[];
}
interface Manage { connections: Connection[]; manual: ManagedAccount[]; order: number[] }

const TYPES: { id: string; label: string }[] = [
  { id: 'loan', label: 'Loan' },
  { id: 'credit_card', label: 'Credit card' },
  { id: 'checking', label: 'Checking' },
  { id: 'savings', label: 'Savings' },
  { id: 'investment', label: 'Investment' },
  { id: 'cash', label: 'Cash' },
  { id: 'other', label: 'Other' },
];

export function settingsScreen(): HTMLElement {
  const root = h('div', 'screen');
  const top = h('div', 'topbar');
  const body = h('div');
  body.appendChild(h('div', 'loading', 'Loading…'));
  top.appendChild(h('div', 'title', 'Settings'));
  root.append(top, body);

  const msg = h('div', 'error');
  const say = (m: string) => { msg.textContent = m; };

  async function load(): Promise<void> {
    try {
      render(await api.get<Manage>('/manage'));
    } catch (err) {
      if (err instanceof Error && err.message === 'Unauthorized') return;
      body.innerHTML = '';
      body.appendChild(h('div', 'error', err instanceof Error ? err.message : 'Could not load'));
    }
  }

  function render(d: Manage): void {
    body.innerHTML = '';
    msg.textContent = '';
    body.appendChild(msg);

    // ── Banks ──
    body.appendChild(h('div', 'section-title', 'Banks'));
    for (const c of d.connections) body.appendChild(connectionBlock(c));
    const linkRow = h('div', 'actions');
    const link = h('button', 'btn primary', 'Link a bank');
    link.addEventListener('click', async () => {
      link.disabled = true;
      await openPlaidLink({ onLinked: load, onError: say });
      link.disabled = false;
    });
    const brokerage = h('button', 'btn', 'Link a brokerage');
    brokerage.addEventListener('click', async () => {
      brokerage.disabled = true;
      await openPlaidLink({ products: ['investments'], onLinked: load, onError: say });
      brokerage.disabled = false;
    });
    linkRow.append(link, brokerage);
    linkRow.style.paddingTop = '12px';
    body.appendChild(linkRow);

    // ── Manual accounts ──
    body.appendChild(h('div', 'section-title', 'Manual accounts'));
    for (const a of d.manual) body.appendChild(accountLine(a, 'Manual'));
    if (d.manual.length === 0) body.appendChild(h('div', 'row-sub', 'For what Plaid can’t see, like a car loan. You update the balance by hand.'));
    const add = h('button', 'btn', 'Add a manual account');
    add.style.marginTop = '12px';
    add.addEventListener('click', addManualSheet);
    body.appendChild(add);

    // ── Order on Home ──
    const all = [...d.connections.flatMap(c => c.accounts), ...d.manual];
    const active = d.order.map(id => all.find(a => a.id === id)).filter((a): a is ManagedAccount => !!a);
    if (active.length > 1) {
      body.appendChild(h('div', 'section-title', 'Order on Home'));
      body.appendChild(orderList(active));
    }

    // ── App ──
    body.appendChild(h('div', 'section-title', 'App'));
    const ver = h('div', 'row');
    const verText = h('div', 'row-main', `Version ${APP_VERSION}`);
    const check = h('button', 'btn small', 'Check for update');
    check.addEventListener('click', async () => {
      check.disabled = true;
      check.textContent = 'Checking…';
      const has = await createUpdateChecker().checkOnce().catch(() => false);
      if (has) window.dispatchEvent(new CustomEvent('mt-check-update'));
      check.textContent = has ? 'Updating…' : 'Up to date';
    });
    ver.append(verText, check);
    const out = h('button', 'btn danger', 'Sign out');
    out.style.marginTop = '12px';
    out.addEventListener('click', async () => {
      out.disabled = true;
      try { await api.post('/logout'); } catch { /* a 401 redirects anyway */ }
      location.hash = '#login';
    });
    body.append(ver, out);
  }

  function connectionBlock(c: Connection): HTMLElement {
    const wrap = h('div');
    const head = h('div', 'row');
    const main = h('div', 'row-main');
    main.appendChild(h('div', '', c.label));
    const status = h('div', 'row-sub', c.issue ? `${capital(c.issue)} · last updated ${ago(c.last_synced_at)}` : `Updated ${ago(c.last_synced_at)}`);
    if (c.issue) status.style.color = 'var(--down)';
    main.appendChild(status);
    head.appendChild(main);
    wrap.appendChild(head);

    for (const a of c.accounts) wrap.appendChild(accountLine(a, null));

    const actions = h('div', 'actions');
    const sync = h('button', 'btn small', 'Sync now');
    sync.addEventListener('click', async () => {
      sync.disabled = true;
      sync.textContent = 'Syncing…';
      try { await api.post(`/connections/sync/${c.id}`); } catch (err) { say(`${c.label}: ${err instanceof Error ? err.message : 'sync failed'}`); }
      void load();
    });
    const fix = h('button', c.issue ? 'btn small primary' : 'btn small', 'Fix login');
    fix.addEventListener('click', async () => {
      fix.disabled = true;
      await openPlaidLink({ connectionId: c.id, onLinked: load, onError: say });
      fix.disabled = false;
    });
    const unlink = h('button', 'btn small danger', 'Unlink');
    unlink.addEventListener('click', async () => {
      if (!confirm(`Unlink ${c.label}? This deletes its ${c.accounts.length === 1 ? 'account' : `${c.accounts.length} accounts`} and all their history from CoinHealth.`)) return;
      unlink.disabled = true;
      try { await api.del(`/connections/${c.id}?deleteData=true`); } catch (err) { say(err instanceof Error ? err.message : 'Unlink failed'); }
      void load();
    });
    actions.append(sync, fix, unlink);
    wrap.appendChild(actions);
    return wrap;
  }

  function accountLine(a: ManagedAccount, sub: string | null): HTMLElement {
    const row = h('button', 'row');
    row.style.width = '100%';
    row.style.textAlign = 'left';
    row.style.paddingLeft = sub ? '0' : '14px';
    const main = h('div', 'row-main');
    main.appendChild(h('div', '', a.name));
    const notes = [sub, a.archived ? 'archived' : null, !a.archived && !a.in_net ? 'not in net' : null].filter(Boolean);
    if (notes.length) main.appendChild(h('div', 'row-sub', notes.join(' · ')));
    const bal = h('div', 'money', money(isDebt(a.type) ? -a.balance : a.balance) + (isDebt(a.type) ? ' owed' : ''));
    if (a.archived) bal.classList.add('muted');
    row.append(main, bal);
    row.addEventListener('click', () => { location.hash = `#account?id=${a.id}`; });
    return row;
  }

  function orderList(accounts: ManagedAccount[]): HTMLElement {
    const list = h('div');
    const ids = accounts.map(a => a.id);
    const draw = () => {
      list.innerHTML = '';
      ids.forEach((id, i) => {
        const a = accounts.find(x => x.id === id)!;
        const row = h('div', 'row');
        row.appendChild(h('div', 'row-main', a.name));
        const upBtn = h('button', 'btn small', '↑');
        const downBtn = h('button', 'btn small', '↓');
        upBtn.disabled = i === 0;
        downBtn.disabled = i === ids.length - 1;
        upBtn.addEventListener('click', () => move(i, -1));
        downBtn.addEventListener('click', () => move(i, 1));
        row.append(upBtn, downBtn);
        list.appendChild(row);
      });
    };
    const move = (i: number, dir: number) => {
      [ids[i], ids[i + dir]] = [ids[i + dir], ids[i]];
      draw();
      api.post('/accounts/order', { ids }).catch(err => say(err instanceof Error ? err.message : 'Could not save order'));
    };
    draw();
    return list;
  }

  function addManualSheet(): void {
    openSheet((sheet, close) => {
      sheet.appendChild(h('h3', '', 'Add a manual account'));
      const name = h('input', 'input');
      name.placeholder = 'Kia car loan';
      const type = h('select', 'input');
      for (const t of TYPES) {
        const o = h('option', '', t.label);
        o.value = t.id;
        type.appendChild(o);
      }
      const amount = h('input', 'input');
      amount.inputMode = 'decimal';
      amount.placeholder = '0';
      const amountLabel = h('span', '', 'Amount owed');
      type.addEventListener('change', () => { amountLabel.textContent = isDebt(type.value) ? 'Amount owed' : 'Balance'; });
      const field = (label: HTMLElement | string, input: HTMLElement) => {
        const f = h('label', 'field');
        f.append(typeof label === 'string' ? h('span', '', label) : label, input);
        return f;
      };
      const err = h('div', 'error');
      const save = h('button', 'btn primary', 'Add');
      save.style.width = '100%';
      save.addEventListener('click', async () => {
        const n = parseFloat(amount.value.replace(/[$,\s]/g, ''));
        if (!name.value.trim() || !isFinite(n)) { err.textContent = 'A name and an amount, please.'; return; }
        save.disabled = true;
        try {
          await api.post('/accounts/manual', { name: name.value.trim(), type: type.value, balance: n });
          close();
          void load();
        } catch (e) {
          save.disabled = false;
          err.textContent = e instanceof Error ? e.message : 'Could not add';
        }
      });
      sheet.append(field('Name', name), field('Type', type), field(amountLabel, amount), err, save);
      requestAnimationFrame(() => name.focus());
    });
  }

  void load();
  return root;
}

function capital(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
